import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  PieChart, Pie, Cell, ResponsiveContainer, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, ZAxis,
} from "recharts";
import {
  Sparkle, DownloadSimple, ArrowLeft, CheckCircle, XCircle, Timer, Gauge,
} from "@phosphor-icons/react";
import { SEV, methodColor, statusColor } from "@/lib/ui";
import { exportUrl } from "@/lib/api";

export const ReportView = ({ run, onBack }) => {
  const [filter, setFilter] = useState("all");
  const stats = run.stats || {};
  const results = run.results || [];

  const donut = useMemo(() => ([
    { name: "Passed", value: stats.passed || 0, color: "hsl(160 70% 42%)" },
    { name: "Failed", value: stats.failed || 0, color: "hsl(0 75% 60%)" },
  ]), [stats]);

  const scatter = useMemo(
    () => results.filter((r) => r.latency_ms).map((r, i) => ({
      x: i + 1, y: r.latency_ms, name: `${r.method} ${r.path}`,
      fill: r.status === "pass" ? "hsl(160 70% 42%)" : "hsl(0 75% 60%)",
    })), [results]);

  const filtered = filter === "all" ? results
    : filter === "failed" ? results.filter((r) => r.status === "fail")
    : results.filter((r) => r.status === "pass");

  const downloadJson = () => window.open(exportUrl(run.id), "_blank");
  const downloadPdf = () => window.print();

  return (
    <div className="mx-auto max-w-[1400px]">
      {/* header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <button data-testid="back-btn" onClick={onBack}
            className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
            <ArrowLeft size={16} weight="bold" /> Back
          </button>
          <h2 className="font-head text-3xl font-bold tracking-tight">{run.target_title || run.name}</h2>
          <p className="mt-1 truncate font-mono text-sm text-muted-foreground">{run.source || run.base_url}</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <button data-testid="download-json" onClick={downloadJson}
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium transition-colors hover:bg-accent">
            <DownloadSimple size={16} weight="bold" /> JSON
          </button>
          <button data-testid="download-pdf" onClick={downloadPdf}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition-all hover:brightness-110">
            <DownloadSimple size={16} weight="bold" /> PDF
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi icon={Gauge} label="Pass rate" value={`${stats.pass_rate ?? 0}%`} accent="hsl(217 91% 60%)" testid="kpi-passrate" />
        <Kpi icon={CheckCircle} label="Passed" value={stats.passed ?? 0} accent="hsl(160 70% 42%)" />
        <Kpi icon={XCircle} label="Failed" value={stats.failed ?? 0} accent="hsl(0 75% 60%)" />
        <Kpi icon={Timer} label="Avg latency" value={`${stats.avg_latency_ms ?? 0}ms`} accent="hsl(38 85% 50%)" />
      </div>

      {/* AI summary + donut */}
      <div className="mb-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-6 lg:col-span-2">
          <div className="mb-3 flex items-center gap-2">
            <Sparkle size={18} weight="fill" className="text-primary" />
            <span className="font-head text-sm font-semibold">AI executive summary</span>
          </div>
          <p data-testid="ai-summary" className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
            {run.summary || "No summary available."}
          </p>
          {stats.severity_counts && Object.keys(stats.severity_counts).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-4">
              {Object.entries(stats.severity_counts).map(([sev, n]) => (
                <span key={sev} className={`rounded-full border px-3 py-1 text-xs font-semibold ${SEV[sev]?.cls || SEV.low.cls}`}>
                  {SEV[sev]?.label || sev}: {n}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <span className="font-head text-sm font-semibold">Pass / fail distribution</span>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={donut} dataKey="value" innerRadius={55} outerRadius={80} paddingAngle={3} strokeWidth={0}>
                  {donut.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-5 text-xs">
            {donut.map((d) => (
              <span key={d.name} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: d.color }} /> {d.name} ({d.value})
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Latency scatter */}
      <div className="mb-5 rounded-2xl border border-border bg-card p-6">
        <span className="font-head text-sm font-semibold">Response time distribution</span>
        <div className="mt-3 h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" dataKey="x" name="test" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis type="number" dataKey="y" name="ms" unit="ms" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <ZAxis range={[60, 60]} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ strokeDasharray: "3 3" }} />
              <Scatter data={scatter}>
                {scatter.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Endpoint table */}
      <div className="rounded-2xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <span className="font-head text-sm font-semibold">Test results ({results.length})</span>
          <div className="flex gap-1 rounded-lg bg-muted p-1 print:hidden">
            {["all", "failed", "passed"].map((f) => (
              <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
                className={`rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors ${
                  filter === f ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                {f}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="results-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-6 py-3 font-medium">Method</th>
                <th className="px-4 py-3 font-medium">Path</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Latency</th>
                <th className="px-4 py-3 font-medium">Severity</th>
                <th className="px-6 py-3 font-medium">Result</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <motion.tr key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.4) }}
                  className={`border-t border-border/60 ${i % 2 ? "bg-muted/30" : ""}`}>
                  <td className="px-6 py-3"><span className={`font-mono text-xs font-bold ${methodColor(r.method)}`}>{r.method}</span></td>
                  <td className="max-w-[240px] truncate px-4 py-3 font-mono text-xs">{r.path}</td>
                  <td className="px-4 py-3 text-xs capitalize text-muted-foreground">{r.category}</td>
                  <td className="px-4 py-3"><span className={`font-mono text-xs font-semibold ${statusColor(r.actual_status)}`}>{r.actual_status || "ERR"}</span></td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{r.latency_ms ? `${r.latency_ms}ms` : "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${SEV[r.severity]?.cls || SEV.none.cls}`}>
                      {SEV[r.severity]?.label || "OK"}
                    </span>
                  </td>
                  <td className="px-6 py-3">
                    {r.status === "pass"
                      ? <span className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(160_70%_42%)]"><CheckCircle size={14} weight="fill" /> Pass</span>
                      : <span className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(0_75%_60%)]" title={r.error}><XCircle size={14} weight="fill" /> {r.error?.slice(0, 40) || "Fail"}</span>}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="px-6 py-10 text-center text-sm text-muted-foreground">No results match this filter.</div>}
        </div>
      </div>
    </div>
  );
};

const tooltipStyle = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 12,
  fontSize: 12,
};

const Kpi = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-border bg-card p-5">
    <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: `${accent}1a`, color: accent }}>
      <Icon size={18} weight="bold" />
    </div>
    <div className="font-head text-2xl font-bold">{value}</div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </div>
);
